/**
 * Canonicalizes an insured name for whitespace- and case-tolerant comparison.
 * Trims outer whitespace, collapses runs of internal whitespace to a single
 * space, and lowercases the result. Used so that policy `customerName` values
 * with incidental whitespace differences (CSR-637, CSR-663) still resolve to
 * the matching insured record on both client and server.
 *
 * The SQL side (server/storage.ts:getInsuredByName) performs the equivalent
 * transform on the column via `lower(trim(regexp_replace(<col>, '\s+', ' ', 'g')))`.
 * Keep this function and that SQL in lock-step.
 */
export function normalizeInsuredName(name: string): string {
  return name.replace(/\s+/g, " ").trim().toLowerCase();
}

/**
 * Trailing business-entity suffixes that the Data Center strips when it
 * consolidates duplicate insureds (CSR-1111) but that policy `customerName`
 * values still carry. Matched as a whole trailing token only — never as a
 * substring — so "INCOME" is not truncated to "OME" and "COSTCO" keeps its
 * "CO". Dotted forms (l.l.c., inc., corp.) and long forms (limited,
 * incorporated, company) are covered too. Each entry is compared against a
 * single already-lowercased token.
 *
 * CSR-1151 (S-001): this is a `Set` of whole tokens rather than a regex on
 * purpose. The previous `/(?:[\s,]+(?:...))+$/` pattern had overlapping
 * quantifiers (`[\s,]+` inside an outer `+`) that backtracked quadratically on
 * a long run of spaces/commas, so an authenticated user could plant a
 * pathological insured name and stall the event loop when it was searched. A
 * token Set lookup is O(1) per token and the surrounding strip is O(n) in the
 * input length.
 */
const ENTITY_SUFFIX_TOKENS = new Set<string>([
  "llc",
  "l.l.c.",
  "l.l.c",
  "inc",
  "inc.",
  "incorporated",
  "corp",
  "corp.",
  "corporation",
  "ltd",
  "ltd.",
  "limited",
  "co",
  "co.",
  "company",
]);

/**
 * CSR-1151: dedup-only normalization for AMS global search. Strengthens
 * `normalizeInsuredName` by additionally stripping trailing entity suffixes so
 * a policy `customerName` ("…GROCERY LLC") matches its already-deduped insured
 * ("…GROCERY") and the phantom duplicate client card is skipped.
 *
 * This is deliberately a SEPARATE helper from `normalizeInsuredName`: that
 * function is the upsert/matching key for the policy sync (server/sync.ts) and
 * is mirrored in SQL by storage.getInsuredByName. Stripping suffixes there
 * would change insured-matching/upsert semantics and could merge genuinely
 * distinct entities. Suffix stripping is only safe for the read-only search
 * dedup comparison, so it lives here and is used only by GET /api/search.
 */
export function normalizeInsuredNameForDedup(name: string): string {
  const base = normalizeInsuredName(name);
  if (!base) return base;

  // Linear-time token strip (CSR-1151 / S-001). Split on whitespace and commas
  // into tokens, then pop trailing tokens while they match a known entity
  // suffix. Each step is O(1) and we touch each token at most twice, so the
  // whole operation is O(n) in the input length — no backtracking.
  const tokens = base.split(/[\s,]+/).filter((token) => token.length > 0);
  while (tokens.length > 1 && ENTITY_SUFFIX_TOKENS.has(tokens[tokens.length - 1])) {
    tokens.pop();
  }
  const stripped = tokens.join(" ");

  // Guard against a name that is nothing but a suffix (e.g. "LLC"): fall back
  // to the unstripped form so it stays a distinct, comparable key. The
  // `tokens.length > 1` condition above already keeps the last token, but the
  // join may differ from `base` when the input had commas, so fall back
  // explicitly when nothing survived.
  return stripped.length > 0 ? stripped : base;
}

/**
 * CSR-1245: dedup key for AMS global-search suggestions that distinguishes two
 * genuinely-distinct same-name insureds (e.g. "LW Freight LLC" in OH vs TX,
 * each with its own DOT/externalId) instead of collapsing them on name alone.
 *
 * Why DOT (not state): policy-derived suggestions come from the `policies`
 * table, which has no state column — only `customer_name`, `dot_number`,
 * `company_email`. DOT is the only discriminator available on both the insured
 * and the policy side, so it is the key.
 *
 * Keying rules:
 *  - When a DOT number is present, the key is the DOT (`dot:<normalized>`).
 *    Two same-name entities with different DOTs get different keys → both
 *    surface (the CSR-1245 fix).
 *  - When no DOT is present, fall back to the suffix-stripped name key
 *    (`name:<normalizeInsuredNameForDedup>`). This preserves the CSR-1151
 *    phantom collapse: after DC consolidation the survivor insured keeps its
 *    DOT while the leftover policy customer_name has none, so the phantom
 *    policy suggestion (null DOT) still collapses onto the survivor by name.
 *
 * The `dot:` / `name:` prefixes keep the two key spaces disjoint so a DOT
 * value can never accidentally equal a name value.
 */
export function insuredDedupKey(input: {
  name: string | null | undefined;
  dotNumber?: string | null;
}): string {
  const dot = input.dotNumber?.toLowerCase().trim();
  if (dot) return `dot:${dot}`;
  const name = input.name ? normalizeInsuredNameForDedup(input.name) : "";
  return `name:${name}`;
}
